import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, Clock, ArrowRight } from 'lucide-react';
import { useHomebuyingJourney } from '@/hooks/useHomebuyingJourney';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Futuristic3DCard } from '@/components/futuristic/Futuristic3DCard';
import { JourneyStepDetailModal } from './JourneyStepDetailModal';

export const JourneyHero: React.FC = () => {
  const { 
    steps, 
    currentStep, 
    progressPercentage, 
    totalSteps, 
    navigateToCurrentStep,
    selectedStep,
    isModalOpen,
    openStepModal,
    closeStepModal,
    navigateToStep
  } = useHomebuyingJourney();

  if (!currentStep) return null;

  return (
    <Futuristic3DCard glowColor="cyan" intensity="heavy" className="p-8">
      <div className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4"
        >
          <Badge variant="secondary" className="text-sm">
            Your Homebuying Journey
          </Badge>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            Step {currentStep.order} of {totalSteps}: {currentStep.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {currentStep.description}
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Progress</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
          </div>
          <Progress value={progressPercentage} className="h-3" />
        </motion.div>

        {/* Journey Steps Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-5 gap-4"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              onClick={() => openStepModal(step)}
              className={`
                flex flex-col items-center p-4 rounded-lg border transition-all duration-300 cursor-pointer hover:scale-105
                ${step.isCurrent 
                  ? 'border-primary bg-primary/10 hover:bg-primary/15' 
                  : step.isCompleted 
                    ? 'border-green-500/50 bg-green-500/10 hover:bg-green-500/15' 
                    : 'border-border/50 bg-background/50 hover:bg-background/70'
                }
              `}
            >
              <div className="mb-2">
                {step.isCompleted ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : step.isCurrent ? (
                  <Circle className="w-6 h-6 text-primary fill-current" />
                ) : (
                  <Circle className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <div className="text-center">
                <h3 className={`text-sm font-medium ${
                  step.isCurrent ? 'text-primary' : 
                  step.isCompleted ? 'text-green-400' : 'text-muted-foreground'
                }`}>
                  {step.title}
                </h3>
                <div className="flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{step.estimatedTime}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Primary Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <Button
            onClick={navigateToCurrentStep}
            size="lg"
            className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white px-12 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105"
          >
            Continue Journey
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm text-muted-foreground mt-3">
            Estimated time: {currentStep.estimatedTime}
          </p>
        </motion.div>
      </div>
      
      <JourneyStepDetailModal
        step={selectedStep}
        isOpen={isModalOpen}
        onClose={closeStepModal}
        onNavigateToStep={navigateToStep}
      />
    </Futuristic3DCard>
  );
};